import React, { ReactNode } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../../../styles/modernListingPageSx";

interface InventoryAdjustmentDialogShellProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  disableClose?: boolean;
}

/** Shared chrome for the Adjust, SKU Transfer and CSV import popups. */
const InventoryAdjustmentDialogShell: React.FC<
  InventoryAdjustmentDialogShellProps
> = ({ open, title, subtitle, onClose, children, footer, disableClose }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isDark = theme.palette.mode === "dark";
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    if (!disableClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isPhone}
      sx={{ zIndex: 2000 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: isPhone ? 0 : "16px",
            overflow: "hidden",
            ...(!isPhone && {
              maxHeight: "90vh",
              width: { sm: "95vw", md: "85vw", lg: "70vw" },
              maxWidth: { sm: "95vw", md: "85vw", lg: "70vw" },
              margin: "32px",
            }),
            bgcolor: isDark ? "#1E293B" : colors.surface,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${isDark ? "#334155" : colors.border}`,
          background: isDark
            ? "#1E293B"
            : `linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceMuted} 100%)`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <CustomTypography
            variant="heading4"
            color={isDark ? "#F1F5F9" : colors.title}
          >
            {title}
          </CustomTypography>
          {subtitle && (
            <CustomTypography
              variant="body2"
              color={isDark ? "#94A3B8" : colors.subtitle}
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {subtitle}
            </CustomTypography>
          )}
        </Box>
        <IconButton onClick={handleClose} disabled={disableClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          "& > *": { width: { xs: "100%", sm: "auto" } },
          borderTop: `1px solid ${isDark ? "#334155" : colors.border}`,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        }}
      >
        {footer}
      </DialogActions>
    </Dialog>
  );
};

export default InventoryAdjustmentDialogShell;
